import { Feature, Point, FeatureCollection } from 'geojson';
import mapboxgl, { GeoJSONSource, LngLatLike } from 'mapbox-gl';
import MapboxglSpiderfier from 'mapboxgl-spiderifier';
import 'mapbox-gl/dist/mapbox-gl.css';

import {
    addNavigation,
    addImages,
    setAllMarkersUnactive,
    getBoundsFromFeatures,
    getPlaceById,
    setSelectedElement,
    removePopup,
    addAttribution,
    addSearch,
    addFilter,
} from '../function/mapHelpers';

// custom style "mapbox://styles/username/ckzcr7hd0003b14qrsjvmzx4m" =>
// https://api.mapbox.com/styles/v1/username/ckzfpdd9t00ay15pclynqtwgn?access_token=pk.access.token

// pre style from mapbox =>
// https://api.mapbox.com/v4/mapbox.mapbox-streets-v8,mapbox.mapbox-terrain-v2.json?secure=&access_token=pk.access.token

mapboxgl.accessToken = process.env.MAPBOX_ACCESS_TOKEN;
const tilesApi = process.env.MAPBOX_TILES_API;

interface ImageObject {
    src: string;
    name: string;
}
interface CreateMap {
    mapId: string;
    places: FeatureCollection;
    activePlaceId?: number;
    layerName: string;
    center?: LngLatLike;
    initialZoom?: number;
    fitToMarkers?: boolean;
    useSearch?: boolean;
}

const activeMarker =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACIAAAApCAYAAABQgPsBAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAImSURBVHgB7ZhPbtpAFMa/55REaVKJ7CLBwr0ByK3URaUmN+i6mzYnqHqCkhukJyirbpMbsOmiiybyDepFibrDVQnEGHv6HhUpmIDHA7FZ8FuBPYafvvnjN0PQ4Kb6rEFKfYQBEdHpk5/fG2ntKK1Bt+qcWQrvsQQ6MgtFuhWnaQFvsQIU0NxrX54gi4iyj8r94Z/P/PRrrBKFi6AXnRz4ro80kZFE2G3xpxoeAqXcoBcfJ2WmRPr2C1uFw/MHk/iPR6XS8a73zZsR+ScRchKwkQ9TMlSQxIwMde3nNSuMzwuQmJKhm6rTIYUyCoSntm8VLSHw+ChbWBMewZDdr1/uvd5/+QYmrE0iG5EkG5Ek1Ks4CmvApmuSrI+IIvgoGAXlW4NhVOfyzUNR8H8Porg+Kow6hzV7x7JaILKRJywRxFy//nK9u1Ixd5kJCfl6N1jlgtzIpZukkp+QEGa2Ex27Vt4JORlQrtuJmel74Ll+UOJkCBdYuYNq3ich5LflZIm966u5W86FC9p++/IdnwI0sCyxOlskIaSeBgj9itPiN+MRDEhLYozWEn8bRak/NI9BHJ/qtNMSGS04Bl0kaUxO0aVFhNvt+FOm95Is3ZppZBKRaW3xoNNtLwczumkIWoN1jCx228OtH6m7Q07j8fXVU2QgUz0iqXC/f0hrR0QNZCRTImMWTmeDNASzCo1PCeffyp7G6DkYEh7WXyWvBVvq937bdWHAX0bBE1Pb8UFTAAAAAElFTkSuQmCC';
const unactiveMarker =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACIAAAApCAYAAABQgPsBAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAMHSURBVHgB7ZhNctowFMefTCFTks7ALhNYKDcwcTPTXckN2hOUnKDpCUJukJ4g3IDcgCy7KBnfAC9Kpjvo1ECMP1TJhVSWvyQbdv1vAMmWfvwtvfdkBBJatN/2ESHXUEA+Qjdvfnzv512H8i6w28atRuAzlJAMTCaI3TIGGsAn2IEIwOBwOr4EFRCCu42V9/uO3v0BdikC987Sv2zOzTnkgYQQrj2i3/RoB7GcILho/jQtkNDsWMcHmjYChLAwjuks6TgCTARkhd9h4nrDshC5MAAWqlYvXlvfrBjIXwiXOgF4FxCqMCgHItFGZRisNw5cCgNIT4NBNj7XNTcYxiAAHpyF/7EsRATGqyRtgBAGLdrGDBFo8D2EkMHh02PqVkt1MOHZi0oKCXRrzzURAgJyWxCCKexj16TdfzQd92iU7vNtdH000LJlEL6xPh2jghC8cp0R59VAQZIQTFtgab0CNWH+h+ie8C8xKEjJkX3qP4gotTVCwz0fqsWVH7tWQUqOsJwjNcEmP4GClEBY4suFKZgklddIJkyJTB2LrLLFbhkl1cEaQRDJrhVarS9axh3sSWHSEyAIkLm29vyOaDMNl73liTGcNfQG7EhhCdoyRrFinM699oNOGKJV68tCEDl18EuuUKkvVSRbgr7smozdkFtjFIAwxd0Vqz1k6ksoC5HwuGNxpGmZc6dKnUFwL3SFA9utcx0KQrASNG3NFTlyPtA6JDN809g0SYLIKkEzI2tSfUnVpdsw9Txst856IkReHcyU+zaAie1/Gn67XJNFjxqdJItFN/Kc2Eoq1zz7vjgQrtW12KuKJDfWQXADEpICCQOO+IgQuuIjL1ugGqDIyxzmhmwClM6+z7XgK5+X2FmEd8X33Cvg3WChW9KNzXjyWp2cXROE+pEBqtXTcF7XnfDt1I0+XRv7AWHBruZVJsLp0Np8Yo7Cqj89noKC1Co0GuzoP/0iNGMQFigSXJORkiNbJWznfyrgBlOx4wSt4tK71N0I74OCco8778U2p0J+HU1NEwroD9n9LDzMJ2quAAAAAElFTkSuQmCC';
const hoverMarker =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACIAAAApCAYAAABQgPsBAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAKxSURBVHgB7VjBcdswELyjbI/zUwlyB04FsTqQrWQmv1AVxKnAcgdOBeYzjzhRB3IHdgdmB1FemZFMIDhETHggSOJAacaP7EcjEDguF3e8BRECkL4t5qDhCmKAcJ19Hcy7p3WRmBY35ucj9EEAGewgkZmfD7AbZNndYAYSIulED2GgbwH0BHYKXECBs2yBq9oV8JFI9BJQn7ILGnJQ63G2eJVDANL3egQbEwfML4uDj6Bw7JJJaot3QIKQfcEcDnFsnpWvodiJekgnv0ZsuPsJ5CSqCI2L7ZP9MorJBGw3pu/Wp6AOvtdIgL6HYnDelwQj4yuALRk0JfrD/B0661pLrc82NrwSVkmNhIabKBIEBJPsR0s3EaswsVNzj7kzPCRFtDMRo0hUEaYMu28CEoSQIGyVAQEOQAROwlWPPSWREUCmyB7xn4gLYY5Q3/iXJ27mM1DlCCBTxNfEmkiY8gUBREQaO6qHhLRJinOklUyPTl17s4aa3T7w+WBShHdX49bNxFvYE7ZNzzXjK2zuH83+MorAHxtAduOMXShtgJ30AoyRTVapvxSRoIcc6IcuH/y3aprJdHuMVhKNSvPqepnHCYKdoKwyC3ahVIY8biwJE74p5yKOnHif3SXj9nXqyUeizYK2vln9/lKfzabPjefh9OI59ZjqVh9M6PwaYINP1ZLVv82X5LVP4poa2ijxrZ0EIazXHCIPZPNF1T5VeNVQ62sIQBARW9ruFiFc2gorSVCCYsI/5pAagVUW3n1V8hl4XxoyVTbq0tmSPFQNQlCOlEgviiuzYs4Gi/UJHB8TkSc2bhQ0ubEnIrZx2RsOKzfMt5FG1TFD4gQEkDk0qhKtP7FBIuCcYRD1HIQQKVKiVs5VRKhBiDtOYNG49zFq2HUQidm5fuOOab35mS2OHiECvwEpR9tUzOOH+QAAAABJRU5ErkJggg==';

const images: ImageObject[] = [
    { src: activeMarker, name: 'active-marker' },
    { src: unactiveMarker, name: 'unactive-marker' },
    { src: hoverMarker, name: 'hover-marker' },
];

const MAX_ZOOM = 17; // Setting this above 17 will break cluster behavior
const MAX_CLUSTER_ZOOM = 14;
const MIN_SPIRAL_VALUE = 4;

const createMap = ({
    mapId,
    places,
    activePlaceId,
    layerName,
    center = [10.7316, 59.9371],
    initialZoom = 12,
    fitToMarkers = true,
    useSearch = false,
}: CreateMap) => {
    var map = new mapboxgl.Map({
        container: mapId,
        style: tilesApi,
        center, // starting position [lat, lng]
        zoom: initialZoom,
        maxZoom: MAX_ZOOM,
        attributionControl: false,
    });

    const spiderifier = new MapboxglSpiderfier(map, {
        onClick: (e: Event) => e.preventDefault(),
        initializeLeg,
        animate: true,
        animationSpeed: 200,
        circleSpiralSwitchover: MIN_SPIRAL_VALUE,
    });

    function initializeLeg(spiderLeg) {
        const pinElem: HTMLElement = spiderLeg.elements.pin;
        const feature = spiderLeg.feature;
        let popup: string;

        pinElem.addEventListener('click', function (e) {
            e.stopPropagation();
            popup = setSelectedElement({ map, feature, layerName, changeIcons: false }).popup;
            spiderLeg.mapboxMarker.setPopup(popup);
        });
    }

    map.on('load', () => {
        addNavigation(map);
        addImages(map, images);
        addAttribution();
        addFilter({ map, layerName, places, spiderifier });
        if (useSearch) addSearch({ map, layerName, places, spiderifier });

        map.scrollZoom.disable();

        map.addSource(layerName, {
            type: 'geojson',
            data: places,
            cluster: true,
            clusterMaxZoom: MAX_ZOOM,
            clusterRadius: 30,
        });

        map.addLayer({
            id: layerName,
            type: 'symbol',
            source: layerName,
            layout: {
                'icon-image': 'unactive-marker',
                'icon-size': 0.75,
                'icon-allow-overlap': true,
            },
        });

        // Set map center based on projects bounds or center on single project
        if (activePlaceId) {
            const activeProject = getPlaceById(places.features, activePlaceId);
            map.jumpTo({
                center: (activeProject.geometry as Point).coordinates as LngLatLike,
                zoom: initialZoom,
            });
            setSelectedElement({ map, layerName, feature: activeProject });
        } else if (fitToMarkers) {
            const bounds = getBoundsFromFeatures(places.features);
            map.fitBounds(bounds, { padding: 100, duration: 0 });
        }

        map.addLayer({
            id: 'clusters',
            type: 'circle',
            source: layerName,
            filter: ['has', 'point_count'],
            paint: {
                'circle-color': '#603345',
                'circle-radius': 20,
            },
        });
        map.addLayer({
            id: 'cluster-count',
            type: 'symbol',
            source: layerName,
            filter: ['has', 'point_count'],
            layout: {
                'text-field': '{point_count_abbreviated}',
                'text-size': 14,
            },
            paint: {
                'text-color': '#FFFFFF',
            },
        });

        let hoveredPlaceId = null;

        map.on('mouseenter', layerName, (e) => {
            map.getCanvas().style.cursor = 'pointer';

            const isCluster = (e.features[0] as Feature).properties.cluster;
            if (isCluster) return;

            const id = e.features[0].id;
            const isActive = e.features[0].layer.layout['icon-image'].name === 'active-marker';
            if (!isActive) {
                map.setLayoutProperty(layerName, 'icon-image', [
                    'match',
                    ['id'], // get the feature id
                    id,
                    'hover-marker', //image when id is the hovered feature id
                    'unactive-marker', // default
                ]);
                hoveredPlaceId = id;
            }
        });
        map.on('mouseleave', layerName, () => {
            map.getCanvas().style.cursor = '';
            if (hoveredPlaceId) {
                map.setLayoutProperty(layerName, 'icon-image', [
                    'match',
                    ['id'], // get the feature id
                    hoveredPlaceId,
                    'unactive-marker',
                    'unactive-marker',
                ]);
            }
            hoveredPlaceId = null;
        });
        map.on('click', (e) => {
            if (e.defaultPrevented === false) {
                setAllMarkersUnactive(map, layerName);
            }
        });
        map.getSource(layerName);
        // Cluster click
        map.on('click', 'clusters', (e) => {
            const features = map.queryRenderedFeatures(e.point, {
                layers: ['clusters'],
            });

            const feature = features[0] as Feature;
            const clusterId = feature.properties.cluster_id;
            if (map.getZoom() < MAX_CLUSTER_ZOOM) {
                (map.getSource(layerName) as GeoJSONSource).getClusterExpansionZoom(clusterId, (err, zoom) => {
                    if (err) return console.error('Error while getting cluster expansion zoom', err);
                    const zoomLvl = zoom > MAX_CLUSTER_ZOOM ? MAX_CLUSTER_ZOOM : zoom;
                    map.easeTo({ center: (feature.geometry as Point).coordinates as LngLatLike, zoom: zoomLvl });
                });
            } else {
                (map.getSource(layerName) as GeoJSONSource).getClusterLeaves(feature.properties.cluster_id, 100, 0, (err, leafFeatures) => {
                    if (err) return console.error('error while getting leaves of a cluster', err);
                    spiderifier.spiderfy((feature.geometry as Point).coordinates, leafFeatures);
                });
            }
        });

        // Place click
        map.on('click', layerName, (e) => {
            e.preventDefault();
            const point = e.features[0];
            const isCluster = point.properties.cluster;

            if (isCluster) return;

            hoveredPlaceId = null;
            const feature = getPlaceById(places.features, point.id as number)
            setSelectedElement({ map, feature, layerName });
        });
    });

    return map;
};

export default createMap;
